"use client";

import {
  isToolPart,
  toolPartName as getToolName,
  toolPartOutput as getToolResult,
  VISUAL_TOOL_NAMES,
} from "@/app/api/chat/tools/parts";

export interface Suggestion {
  label: string;
  message: string;
}

interface SuggestedFollowupsProps {
  result: { suggestions: Suggestion[] } | null;
  state: string;
  sessionId: string;
  carrierId: string;
  onSendMessage?: (text: string) => void;
}

// Inline rendering disabled — SuggestedFollowups now renders in the chip strip
// area of ChatPanel/HomeChatPanel via extractFollowupChips().
export function SuggestedFollowups(_props: SuggestedFollowupsProps) {
  return null;
}

// Low-level tool-part readers (isToolPart / getToolName / getToolResult) and the
// canonical VISUAL_TOOL_NAMES set are shared with the server via tools/parts.ts —
// see that file for the three part shapes they handle. normalizeToolPart (below)
// stays here because it targets the nested shape ToolInvocation.tsx expects.

const TOOL_INVOCATION = "tool-invocation";

/** Normalize any tool part to the nested structure ToolInvocation.tsx expects. */
export function normalizeToolPart(part: any): any {
  if (!isToolPart(part)) return part;
  if (part.toolInvocation) return part; // already nested
  return {
    ...part,
    type: TOOL_INVOCATION, // v6 "tool-<name>"/"dynamic-tool" → the routed type
    toolInvocation: {
      toolCallId: part.toolCallId,
      toolName: getToolName(part),
      args: part.args ?? part.input,
      input: part.input ?? part.args,
      result: part.result ?? part.output,
      output: part.output ?? part.result,
      state: part.state,
    },
  };
}

/** Filter message parts: suppress text when visual tools are present. */
export function filterPartsForRender(parts: any[]): any[] {
  const hasVisualTool = parts.some(
    (p) => isToolPart(p) && VISUAL_TOOL_NAMES.has(getToolName(p) ?? "")
  );
  if (!hasVisualTool) return parts;
  return parts.filter((p) => p.type !== "text");
}

// Tool states where a card renders a skeleton loader (result not in yet).
const TOOL_STREAMING_STATES = new Set([
  "partial-call",
  "input-streaming",
  "call",
]);

/** Whether a tool-invocation part produces any visible UI. Mirrors the null
 *  conditions inside each tool card so we don't paint an empty bubble around
 *  a tool that renders nothing (e.g. suggestFollowupsTool, or an alternatives
 *  search that returned zero results). */
function toolPartRenders(part: any): boolean {
  const state = part.toolInvocation?.state ?? part.state;
  if (TOOL_STREAMING_STATES.has(state)) return true; // skeleton loader shows
  const name = getToolName(part);
  const result = getToolResult(part);
  switch (name) {
    case "getCarrierContactTool":
      return Boolean(
        result?.gated || result?.capped || (result?.hasContact && result?.contact)
      );
    case "findAlternativeCarriersTool":
      // Renders either carrier cards or an empty-state with city suggestions.
      return Boolean(result?.alternatives?.length || result?.suggestions?.length);
    case "contactViaWhatsApp":
      return true; // always renders the button (falls back to a default msg)
    default:
      // suggestFollowupsTool + quote tools render nothing inline
      return false;
  }
}

const CARRIER_TAG = /\[carrier:[^\]]+\]\s*/g;

/** Whether an assistant message has anything visible to render. Used to skip
 *  the bubble wrapper entirely for messages that reduce to nothing (all text
 *  suppressed by a visual tool, all tool parts rendering null). Non-assistant
 *  messages always render. */
export function messageHasVisibleContent(message: any): boolean {
  if (message.role !== "assistant") return true;
  const parts = filterPartsForRender(message.parts || []);
  return parts.some((p: any) => {
    if (p.type === "text") {
      return (p.text ?? "").replace(CARRIER_TAG, "").trim().length > 0;
    }
    if (isToolPart(p)) return toolPartRenders(p);
    if (p.type === "reasoning" || p.type === "source" || p.type === "file") {
      return true;
    }
    return false; // step-start and unknown parts render nothing on their own
  });
}

/** Extract followup chip suggestions from the last assistant message's parts.
 *  Falls back to deterministic chips based on which tool was called. */
export function extractFollowupChips(messages: any[]): Suggestion[] | null {
  for (let i = messages.length - 1; i >= 0; i--) {
    const msg = messages[i];
    if (msg.role !== "assistant") continue;
    const parts = msg.parts || [];

    // Check for explicit suggestFollowupsTool result
    for (const part of parts) {
      if (!isToolPart(part)) continue;
      if (getToolName(part) !== "suggestFollowupsTool") continue;
      const result = getToolResult(part);
      if (result?.suggestions?.length) return result.suggestions;
    }

    // Fallback: deterministic chips based on which visual tool was called
    for (const part of parts) {
      if (!isToolPart(part)) continue;
      const toolName = getToolName(part);
      if (toolName === "getCarrierContactTool") {
        return [
          { label: "No me contestaron", message: "No me contestaron, los datos no funcionan" },
          { label: "Buscar otro transportista", message: "Busca otro transportista" },
          { label: "Listo, gracias", message: "Listo, gracias" },
        ];
      }
      if (toolName === "findAlternativeCarriersTool") {
        return [
          { label: "Buscar en otro estado", message: "Busca transportistas en otro estado" },
          { label: "Listo, gracias", message: "Listo, gracias" },
        ];
      }
    }

    break; // only check the last assistant message
  }
  return null;
}
